import React, { Suspense, lazy, useEffect, useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import i18n from 'i18next'

import { CookieConsent } from 'components/CookieConsent'
import { HEADER_VARIANTS, HeaderV2 } from 'components/HeaderV2'
import {
  i18nInterceptor,
  setI18nInterceptorCallback,
  useI18nInterceptor,
} from 'components/I18nInterceptor'
import { ProgressBar } from 'components/LinearProgressBar'
import { useSectionedProgress } from 'components/SectionedProgressBar'
import { Spinner } from 'components/Spinner'

import {
  selectCurrentVariant,
  selectCurrentVariantCohort,
  selectGrowthbookFeatureKey,
  selectIsGrowthbookReady,
  selectLanguage,
} from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'

import { useActiveSteps } from 'hooks/useActiveSteps'
import { useAmplitudeInitialization } from 'hooks/useAmplitudeInitialization'
import { useApplovinPixelInitialization } from 'hooks/useApplovinPixelInitialization'
import { useClarityInitialization } from 'hooks/useClarityInitialization'
import { useCohortInfo } from 'hooks/useCohortInfo'
import { useEventLoggerInitialization } from 'hooks/useEventLoggerInitialization'
import { useExperimentalFeatures } from 'hooks/useExperimentalFeatures'
import { useFacebookParamsSync } from 'hooks/useFacebookParamsSync'
import { useFacebookPixelInitialization } from 'hooks/useFacebookPixelInitialization'
import { useGAPageTracker } from 'hooks/useGAPageTracker'
import { useGetPageInfo } from 'hooks/useGetPageInfo'
import { useGoogleAnalytics } from 'hooks/useGoogleAnalytics'
import { useGrowthBookInitialization } from 'hooks/useGrowthBookInitialization'
import { useInitPinterestTag } from 'hooks/useInitPinterestTag'
import { useInitialPageRenderMetric } from 'hooks/useInitialPageRenderMetric'
import { useOutbrainPixelInitialization } from 'hooks/useOutbrainPixelInitialization'
import { usePageConfig } from 'hooks/usePageConfig'
import { useRedditPixelInitialization } from 'hooks/useRedditPixelInitialization'
import { useScrollToTop } from 'hooks/useScrollToTop'
import { useSnapPixelInitialization } from 'hooks/useSnapPixelInitialization'
import { useStartSession } from 'hooks/useStartSession'
import { useStuckWatchdog } from 'hooks/useStuckWatchdog'
import { useTaboolaPixelInitialization } from 'hooks/useTaboolaPixelInitialization'
import { useTestEnvQueryParameter } from 'hooks/useTestEnvQueryParameter'
import { useTiktokPixelInitialization } from 'hooks/useTiktokPixelInitialization'
import { useUtmTags } from 'hooks/useUtmTags'

import { FunnelErrorCode } from 'helpers/funnelErrors'
import { getTestCohortToOverride } from 'helpers/getCohortOverride'
import { getEnvironment } from 'helpers/getEnvironment'

import { useUpiPaymentCompletion } from 'modules/purchase/hooks/useUpiPaymentCompletion'

import { Cohort, Language } from 'root-constants'

import { RouteList } from '../../RouteList'

const InContextPopup = lazy(() =>
  import('components/InContextPopup').then((m) => ({
    default: m.InContextPopup,
  })),
)
const TranslationKeysModal = lazy(() =>
  import('components/I18nInterceptor/TranslationKeysModal').then((m) => ({
    default: m.TranslationKeysModal,
  })),
)

export const LayoutProvider: React.FC = () => {
  const { search } = useLocation()

  const uuid = useSelector(selectUUID)
  const currentVariant = useSelector(selectCurrentVariant)
  const cohort = useSelector(selectCurrentVariantCohort)
  const growthbookFeatureKey = useSelector(selectGrowthbookFeatureKey)
  const isGrowthbookReady = useSelector(selectIsGrowthbookReady)
  const language = useSelector(selectLanguage)
  const { isStageEnvironment } = getEnvironment()
  const { addTranslation } = useI18nInterceptor()
  const steps = useActiveSteps()

  const { hasHeader, hasProgressbar, isSecondPage } = useGetPageInfo()
  const { hasSectionedProgress } = useSectionedProgress()
  const { pageBackground, disabledButtonBackground } = usePageConfig()
  const { isPrimaryHeaderCohort } = useCohortInfo()
  const { isAyah49FacebookParamsTest, isKarma36IntroPriceSelect } =
    useExperimentalFeatures()

  const isFacebookTrackingReady = !!currentVariant && isGrowthbookReady

  const overrideCohort = getTestCohortToOverride({
    isKarma36IntroPriceSelect,
  })
  useUtmTags()
  useGoogleAnalytics()
  useGAPageTracker()
  useEventLoggerInitialization(cohort as Cohort)
  useGrowthBookInitialization()
  useAmplitudeInitialization(cohort as Cohort)
  useApplovinPixelInitialization()
  useFacebookParamsSync({
    shouldCreateParams: isAyah49FacebookParamsTest,
    shouldSync: isFacebookTrackingReady,
  })
  useFacebookPixelInitialization(
    isFacebookTrackingReady && !isAyah49FacebookParamsTest,
  )
  useInitPinterestTag()
  useClarityInitialization()
  useOutbrainPixelInitialization()
  useRedditPixelInitialization()
  useScrollToTop()
  useSnapPixelInitialization()
  useStartSession()
  useTaboolaPixelInitialization()
  useTestEnvQueryParameter()
  useTiktokPixelInitialization()
  useUpiPaymentCompletion(isFacebookTrackingReady)

  useLayoutEffect(() => {
    if (!overrideCohort) return

    const searchParams = new URLSearchParams(search)

    searchParams.set('cohort', overrideCohort)
    searchParams.set('uuid', uuid)
    window.location.href = `${window.location.origin}?${searchParams.toString()}`
  }, [search, overrideCohort, uuid])

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--body-background',
      pageBackground,
    )
  }, [pageBackground])

  useLayoutEffect(() => {
    document.documentElement.style.setProperty(
      '--disabled-button-background',
      disabledButtonBackground,
    )
  }, [disabledButtonBackground])

  useEffect(() => {
    i18n.changeLanguage(language)
    document.documentElement.lang = language
    document.documentElement.dir = language === Language.AR ? 'rtl' : 'ltr'
  }, [language])

  useEffect(() => {
    if (isSecondPage) {
      window.pintrk && window.pintrk('track', 'custom')
    }
  }, [isSecondPage])

  useEffect(() => {
    if (!isStageEnvironment) return

    i18nInterceptor(i18n)
    setI18nInterceptorCallback(addTranslation)
  }, [addTranslation, isStageEnvironment])

  const hasUuidInUrl = !!new URLSearchParams(search).get('uuid')
  const shouldWaitForGrowthbook = !!growthbookFeatureKey && !isGrowthbookReady
  const isStuckOnSpinner =
    !overrideCohort && (shouldWaitForGrowthbook || !steps.length)

  const isFunnelRendered = !overrideCohort && !isStuckOnSpinner

  useInitialPageRenderMetric({
    isFirstScreenReady: isFunnelRendered,
  })

  useStuckWatchdog({
    isReady: !isStuckOnSpinner,
    code: FunnelErrorCode.FUNNEL_STEPS_EMPTY_TIMEOUT,
    extra: {
      stepsCount: steps.length,
      growthbookFeatureKey,
      isGrowthbookReady,
    },
  })

  if (overrideCohort || shouldWaitForGrowthbook || !steps.length) {
    return <Spinner />
  }

  return (
    <>
      {hasHeader && (
        <HeaderV2
          variant={
            isPrimaryHeaderCohort
              ? HEADER_VARIANTS.PRIMARY
              : HEADER_VARIANTS.SECONDARY
          }
        />
      )}

      {hasProgressbar && !hasSectionedProgress && <ProgressBar />}

      {isStageEnvironment && (
        <>
          <InContextPopup />
          <TranslationKeysModal />
        </>
      )}

      {hasUuidInUrl && <CookieConsent />}

      <Suspense fallback={<Spinner />}>
        <RouteList />
      </Suspense>
    </>
  )
}
